import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Star } from 'lucide-react';

const CONDITIONS = ['Ice', 'Packed', 'Groomed', 'Fresh', 'Powder'];

const StarRating = ({ value, onChange }) => {
  const [hover, setHover] = useState(null);
  return (
    <div className="visit-star-rating">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          className="visit-star-btn"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(null)}
        >
          <Star
            size={24}
            fill={(hover || value) >= n ? 'var(--pm-accent)' : 'none'}
            stroke={(hover || value) >= n ? 'var(--pm-accent)' : 'var(--pm-text-muted)'}
          />
        </button>
      ))}
    </div>
  );
};

const LogVisitModal = ({ show, onHide, onSave, resort, resorts }) => {
  const [date, setDate] = useState('');
  const [snowRating, setSnowRating] = useState(0);
  const [conditions, setConditions] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Resort search (only used when no resort prop)
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedResort, setSelectedResort] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (show) {
      const today = new Date().toISOString().split('T')[0];
      setDate(today);
      setSnowRating(0);
      setConditions([]);
      setNotes('');
      setSearchQuery('');
      setSelectedResort(resort || null);
      setShowDropdown(false);
    }
  }, [show, resort]);

  const toggleCondition = (c) => {
    setConditions(prev =>
      prev.includes(c.toLowerCase())
        ? prev.filter(x => x !== c.toLowerCase())
        : [...prev, c.toLowerCase()]
    );
  };

  const filteredResorts = searchQuery.length >= 2 && resorts
    ? resorts
        .filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .slice(0, 8)
    : [];

  const handleSelectResort = (r) => {
    setSelectedResort(r);
    setSearchQuery(r.name);
    setShowDropdown(false);
  };

  const handleSave = async () => {
    const target = resort || selectedResort;
    if (!target || !date || snowRating === 0) return;

    setSaving(true);
    try {
      await onSave({
        resortId: target.id,
        resortName: target.name,
        date,
        snowRating,
        conditions,
        notes,
      });
      onHide();
    } catch (error) {
      console.error('Error saving visit:', error);
    } finally {
      setSaving(false);
    }
  };

  const target = resort || selectedResort;
  const canSave = target && date && snowRating > 0;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ background: 'var(--pm-bg-surface)', borderColor: 'var(--pm-border)' }}>
        <Modal.Title style={{ color: 'var(--pm-text)', fontSize: '1.1rem' }}>Log Ski Visit</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: 'var(--pm-bg-surface)' }}>
        {/* Resort selector (only when not on a resort page) */}
        {!resort && (
          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'var(--pm-text)', fontSize: '0.85rem', fontWeight: 600 }}>Resort</Form.Label>
            <div style={{ position: 'relative' }} ref={searchRef}>
              <Form.Control
                type="text"
                placeholder="Search resorts..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setSelectedResort(null);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                style={{ background: 'var(--pm-bg-page)', color: 'var(--pm-text)', borderColor: 'var(--pm-border)' }}
              />
              {showDropdown && filteredResorts.length > 0 && (
                <div className="visit-resort-dropdown">
                  {filteredResorts.map(r => (
                    <div
                      key={r.id}
                      className="visit-resort-dropdown-item"
                      onClick={() => handleSelectResort(r)}
                    >
                      {r.name}
                      {r.country && <span style={{ color: 'var(--pm-text-muted)', fontSize: '0.8rem', marginLeft: '8px' }}>{r.country}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Form.Group>
        )}

        {/* Date */}
        <Form.Group className="mb-3">
          <Form.Label style={{ color: 'var(--pm-text)', fontSize: '0.85rem', fontWeight: 600 }}>Date</Form.Label>
          <Form.Control
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            style={{ background: 'var(--pm-bg-page)', color: 'var(--pm-text)', borderColor: 'var(--pm-border)' }}
          />
        </Form.Group>

        {/* Snow Quality */}
        <Form.Group className="mb-3">
          <Form.Label style={{ color: 'var(--pm-text)', fontSize: '0.85rem', fontWeight: 600 }}>Snow Quality</Form.Label>
          <StarRating value={snowRating} onChange={setSnowRating} />
        </Form.Group>

        {/* Conditions */}
        <Form.Group className="mb-3">
          <Form.Label style={{ color: 'var(--pm-text)', fontSize: '0.85rem', fontWeight: 600 }}>Conditions</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {CONDITIONS.map(c => (
              <button
                key={c}
                type="button"
                className={`visit-condition-badge${conditions.includes(c.toLowerCase()) ? ' visit-condition-badge--active' : ''}`}
                onClick={() => toggleCondition(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </Form.Group>

        {/* Notes */}
        <Form.Group className="mb-1">
          <Form.Label style={{ color: 'var(--pm-text)', fontSize: '0.85rem', fontWeight: 600 }}>Notes <span style={{ fontWeight: 400, color: 'var(--pm-text-muted)' }}>(optional)</span></Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="How was the day?"
            style={{ background: 'var(--pm-bg-page)', color: 'var(--pm-text)', borderColor: 'var(--pm-border)', resize: 'none' }}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ background: 'var(--pm-bg-surface)', borderColor: 'var(--pm-border)' }}>
        <Button variant="outline-secondary" size="sm" onClick={onHide}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? 'Saving...' : 'Save Visit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LogVisitModal;
